import { useState, useEffect } from "react";
import { getPlans, updateCompany, getCompany } from "../../../api/General/Company";
import {
  SubscriptionPlan,
  CompanyInput,
  CountryView,
  CompanyView,
  TimeZoneInfoView,
  Roles,
} from "../../../models/general";
import { getCountries, getTimeZones } from "../../../api/General/Country";
import { useTranslation } from "react-i18next";
import { getRoles } from "../../../api/General/account";
import { generatePalette, setCSSColorVariables } from "../../../utils";
import PhoneInput from 'react-phone-number-input';
import { useSelector, useDispatch } from "react-redux";
import { setCompanyPrimaryColor, updateCompanyRules } from "../../../store/redux/actions/userActionCreator";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Spinner } from "../../../components/ui/Spinner";
import { PageLayout } from "../../../components/ui/PageLayout";
import { Building2 } from "lucide-react";
import { useComplianceCountry } from "../../../hooks/useComplianceCountry";

export default function GeneralDetails() {
  const { t } = useTranslation();
  const dispatchRedux = useDispatch();
  const complianceCountry = useComplianceCountry();
  const { userId, company: reduxCompany, role } = useSelector((s: any) => ({
    userId: s.user.userId,
    company: s.user.company,
    role: s.user.role,
  }));
  const id = reduxCompany.id;

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [countries, setCountries] = useState<CountryView[]>([]);
  const [timeZones, setTimeZones] = useState<TimeZoneInfoView[]>([]);

  const [companyName, setCompanyName] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [websiteLink, setWebsiteLink] = useState("");
  const [countryId, setCountryId] = useState(0);
  const [timeZone, setTimeZone] = useState("");
  const [locales, setLocales] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#304EA1");
  const [employerNarrative, setEmployerNarrative] = useState("");
  const [molNumber, setMolNumber] = useState("");
  // WPS configuration (UAE)
  const [bankRoutingCode, setBankRoutingCode] = useState("");
  const [wpsEmployeeThreshold, setWpsEmployeeThreshold] = useState(100);
  // Employer identifiers (KSA)
  const [commercialRegistrationNumber, setCommercialRegistrationNumber] = useState("");
  const [mudadEstablishmentNumber, setMudadEstablishmentNumber] = useState("");
  const [qiwaEntityId, setQiwaEntityId] = useState("");
  const [crFormatError, setCrFormatError] = useState<string | null>(null);
  const [planId, setPlanId] = useState(0);
  const [organizationId, setOrganizationId] = useState(0);
  const [rolesMaxUsers, setRolesMaxUsers] = useState<{[key: string]: number}>({});
  const [companyImage, setCompanyImage] = useState<File | null>(null);
  const [adminFirstName, setAdminFirstName] = useState("");
  const [adminLastName, setAdminLastName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");

  const [company, setCompany] = useState<CompanyView>({} as CompanyView);
  const [roles, setRoles] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    try {
      const palette = generatePalette(primaryColor);
      setCSSColorVariables(palette);
    } catch {}
  }, [primaryColor]);

  const fetchData = async () => {
    setLoading(true);
    if (id) {
      const companyRes = await getCompany(id);
      if (companyRes.errors?.length === 0 && companyRes.payload) {
        const companyData = companyRes.payload;
        setCompanyName(companyData.name || "");
        setCompanyEmail(companyData.email || "");
        setPhoneNumber(companyData.phoneNumber || "");
        setAddress(companyData.address || "");
        setWebsiteLink(companyData.websiteLink || "");
        setCountryId(companyData.country?.id || 0);
        setTimeZone(companyData.timeZone || "");
        setLocales(companyData.locales || "");
        setPrimaryColor(companyData.primaryColor || "#304EA1");
        setEmployerNarrative(companyData.employerNarrative || "");
        setMolNumber(companyData.molNumber || "");
        setBankRoutingCode(companyData.bankRoutingCode || "");
        setWpsEmployeeThreshold(companyData.wpsEmployeeThreshold ?? 100);
        setCommercialRegistrationNumber(companyData.commercialRegistrationNumber || "");
        setMudadEstablishmentNumber(companyData.mudadEstablishmentNumber || "");
        setQiwaEntityId(companyData.qiwaEntityId || "");
        setPlanId(companyData.subscriptionPlanView?.id || 0);
        setOrganizationId(companyData.organization?.id || 0);
        setRolesMaxUsers(companyData.rolesMaxUsers || {});
        setAdminFirstName("");
        setAdminLastName("");
        setAdminEmail("");
        setCompany(companyData);
      }
    }

    const [plansRes, countriesRes, timeZonesRes, rolesRes] = await Promise.all([
      getPlans(),
      getCountries(),
      getTimeZones(),
      getRoles()
    ]);

    if (plansRes.errors?.length === 0) setPlans(plansRes.payload);
    if (countriesRes.errors?.length === 0) setCountries(countriesRes.payload);
    if (timeZonesRes.errors?.length === 0) setTimeZones(timeZonesRes.payload);
    if (rolesRes.errors?.length === 0) setRoles(rolesRes.payload || []);
    setLoading(false);
  };

  const updateOldCompany = async () => {
    try {
      setLoading(true);
      const combinedInput: CompanyInput = {
        name: companyName,
        email: companyEmail,
        phoneNumber,
        address,
        websiteLink,
        countryId,
        timeZone,
        locales,
        primaryColor,
        employerNarrative,
        molNumber,
        bankRoutingCode,
        wpsEmployeeThreshold,
        commercialRegistrationNumber,
        mudadEstablishmentNumber,
        qiwaEntityId,
        planId,
        organizationId,
        rolesMaxUsers,
        image: companyImage,
        adminFirstName,
        adminLastName,
        adminEmail,
      };
      await updateCompany(id, combinedInput);
      if (combinedInput.primaryColor) {
        dispatchRedux(setCompanyPrimaryColor(combinedInput.primaryColor));
      }
      const selectedCountry = countries.find(c => c.id === countryId);
      const newCompliance = selectedCountry?.iso?.toLowerCase() === 'ae' ? 'UAE'
        : selectedCountry?.iso?.toLowerCase() === 'sa' ? 'KSA' : 'None';
      dispatchRedux(updateCompanyRules({ complianceCountry: newCompliance }));
      setSuccess(true);
      setLoading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  const validateInputs = () => {
    if (countryId == null || countryId === 0 || companyName == null || companyName === "") {
      return false;
    }
    // CR must be 10 digits when provided (KSA Mudad requirement).
    if (commercialRegistrationNumber && !/^\d{10}$/.test(commercialRegistrationNumber)) {
      setCrFormatError(t('settings.compliance.commercialRegistrationFormatInvalid', 'Commercial Registration must be 10 digits'));
      return false;
    }
    setCrFormatError(null);
    return true;
  };

  const addOrUpdate = async () => {
    if (validateInputs()) await updateOldCompany();
  };

  const { rolesUsersAvailable } = company;

  const countryOptions = countries.map(c => ({ value: c.id, label: c.name }));
  const timeZoneOptions = Array.from(
    new Map(timeZones.map(tz => [tz.id, { value: tz.id, label: tz.displayName }])).values()
  );

  return (
    <PageLayout
      title={t("Company Settings")}
      description={t("Manage your company information and preferences")}
      icon={Building2}
      accentColor="slate"
      headerActions={(
        <Button
          variant="outline"
          disabled={loading}
          onClick={addOrUpdate}
          iconName="Save"
        >
          {loading ? <Spinner size="sm" /> : t("Save Changes")}
        </Button>
      )}
    >
      {success && (
        <div role="alert" className="rounded-lg border border-emerald-500/50 bg-emerald-500/10 p-4 text-sm text-emerald-700 dark:text-emerald-400 mb-4 flex items-center justify-between">
          <span>{t("Company Updated Successfully")}</span>
          <button onClick={() => setSuccess(false)} className="hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm">&times;</button>
        </div>
      )}

      <div className="section grid-auto-fit">
        {role === Roles.Operator && (
          <div className="col-span-full">
            <Input
              label={t("Company Name")}
              required
              placeholder={t("Enter company name")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
              value={companyName}
            />
          </div>
        )}

        <Input
          label={t("Email")}
          type="email"
          placeholder={t("Enter email address")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyEmail(e.target.value)}
          value={companyEmail}
        />

        <Select
          label={t("Country")}
          required
          options={countryOptions}
          value={countryId}
          onChange={(val: any) => setCountryId(val)}
          searchable
          placeholder={t("Select country")}
        />

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">{t('Phone Number')}</label>
          <PhoneInput
            international
            placeholder={t("Enter phone number")}
            className="phone-input-reset"
            value={phoneNumber}
            onChange={(value: any) => setPhoneNumber(value)}
          />
        </div>

        <Input
          label={t("Address")}
          placeholder={t("Enter address")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
          value={address}
        />

        <Input
          label={t("Website")}
          placeholder={t("Enter website URL")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWebsiteLink(e.target.value)}
          value={websiteLink}
        />

        <Select
          label={t("TimeZone")}
          options={timeZoneOptions}
          value={timeZone}
          onChange={(val: any) => setTimeZone(val)}
          searchable
          placeholder={t("Select timezone")}
        />

        <Input
          label={t("Employer Narrative")}
          placeholder={t("Enter employer narrative")}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployerNarrative(e.target.value)}
          value={employerNarrative}
        />

        {/* WPS Configuration (UAE compliance tenants) */}
        {complianceCountry === 'UAE' && (
          <>
            <Input
              label={t("MOL Number")}
              placeholder={t("Enter MOL number")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMolNumber(e.target.value)}
              value={molNumber}
            />
            <Input
              label={t('settings.compliance.bankRoutingCode', 'Bank Routing Code')}
              placeholder={t('settings.compliance.bankCodePlaceholder', 'Enter bank routing code')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBankRoutingCode(e.target.value)}
              value={bankRoutingCode}
            />
            <Input
              label={t('settings.compliance.wpsThreshold', 'WPS Employee Threshold')}
              type="number"
              min={1}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setWpsEmployeeThreshold(parseInt(e.target.value) || 100)}
              value={wpsEmployeeThreshold}
            />
          </>
        )}

        {/* Employer Identifiers (KSA compliance tenants) */}
        {complianceCountry === 'KSA' && (
          <>
            <div>
              <Input
                label={t('settings.compliance.commercialRegistration', 'Commercial Registration')}
                placeholder={t('settings.compliance.commercialRegistrationPlaceholder', '10-digit CR number')}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setCommercialRegistrationNumber(e.target.value); setCrFormatError(null); }}
                value={commercialRegistrationNumber}
                aria-invalid={!!crFormatError}
              />
              {crFormatError && <p className="text-xs text-red-600 mt-1">{crFormatError}</p>}
            </div>
            <Input
              label={t('settings.compliance.mudadEstablishmentNumber', 'Mudad Establishment Number')}
              placeholder={t('settings.compliance.mudadEstablishmentNumberPlaceholder', 'Issued by Mudad portal')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMudadEstablishmentNumber(e.target.value)}
              value={mudadEstablishmentNumber}
            />
            <Input
              label={t('settings.compliance.qiwaEntityId', 'Qiwa Entity ID')}
              placeholder={t('settings.compliance.qiwaEntityIdPlaceholder', 'Issued by Qiwa')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQiwaEntityId(e.target.value)}
              value={qiwaEntityId}
            />
          </>
        )}

        {role === Roles.Operator && (
          <Input
            label={t("Locales")}
            required
            placeholder={t("Enter locales")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocales(e.target.value)}
            value={locales}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-6 mt-8 items-start mb-1">
        <div className="flex-1 min-w-[400px]">
          <div className="section p-4">
            <h2 className="text-sm font-semibold text-primary mb-2">{t("Licensed Users")}</h2>
            <div className="flex flex-row flex-wrap gap-2">
              {roles.map((r) => (
                <div key={r} className="flex flex-col items-center flex-1 min-w-[200px] p-3 bg-card rounded-md border border-border">
                  <h3 className="text-sm font-semibold text-foreground mb-4">{r}</h3>
                  <div className="flex items-center gap-2">
                    {id && (
                      <span className="text-sm font-medium text-muted-foreground">
                        {`${rolesUsersAvailable?.[r] ?? 0} /`}
                      </span>
                    )}
                    {role === Roles.Operator ? (
                      <input
                        type="number"
                        min="0"
                        className="w-20 h-8 rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                        onChange={(ev) => {
                          const value = parseInt(ev.target.value || '0');
                          setRolesMaxUsers(prev => ({ ...prev, [r]: value }));
                        }}
                        value={rolesMaxUsers[r] as any}
                      />
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-medium">
                        {rolesMaxUsers[r]}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      <hr className="border-border my-4" />

      <div className="flex justify-end mt-3">
        <Button disabled={loading} onClick={addOrUpdate}>
          {loading ? <Spinner size="sm" /> : t("Save Changes")}
        </Button>
      </div>
    </PageLayout>
  );
}
